package com.devi3east.bashbuddy.auth.data.repositories

import com.devi3east.bashbuddy.auth.data.api.AuthApi
import com.devi3east.bashbuddy.account.organization.domain.models.Organization
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.auth.domain.repositories.AuthRepository
import com.devi3east.bashbuddy.auth.requests.SignInRequest
import com.devi3east.bashbuddy.auth.requests.SignUpRequest
import com.devi3east.bashbuddy.account.organization.requests.UpdateOrganizationRequest
import com.devi3east.bashbuddy.account.profile.requests.UpdateProfileRequest
import com.devi3east.bashbuddy.auth.responses.AuthResult
import com.devi3east.bashbuddy.leaderboard.domain.models.OrganizationLeaderboardItem
import com.devi3east.bashbuddy.supabase.SupabaseClient
import com.russhwolf.settings.Settings
import io.github.jan.supabase.auth.auth
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put

class AuthRepositoryImpl(
    private val api: AuthApi,
    private val settings: Settings
) : AuthRepository {

    override suspend fun signUp(request: SignUpRequest): AuthResult<Profile> {
        return when (val signupResult = api.signUp(request)) {
            is AuthResult.UserAlreadyExists -> {
                signupResult
            }
            is AuthResult.EmailBlank,
            is AuthResult.PasswordBlank,
            is AuthResult.UnknownError -> signupResult
            else -> {
                signIn(SignInRequest(request.email, request.password))
            }
        }
    }

    override suspend fun signIn(request: SignInRequest): AuthResult<Profile> {
        return when (val result = api.signIn(request)) {
            is AuthResult.Authorized -> {
                settings.putString("jwt", result.accessToken ?: "")
                settings.putString("refreshJwt", result.refreshToken ?: "")
                result
            }
            else -> {
                result
            }
        }
    }

    override suspend fun fetchProfile(userId: String): Result<Profile> {
        val token = getAuthToken()
            ?: return Result.failure(Exception("No auth token"))
        return when (val result = api.authenticate(token)) {
            is AuthResult.Authorized -> Result.success(result.data!!)
            else -> Result.failure(Exception("Authentication failed"))
        }
    }

    override suspend fun authenticate(): AuthResult<Profile> {
        val token = getAuthToken()
            ?: return AuthResult.Unauthorized()
        return api.authenticate(token)
    }

    override suspend fun signOut() {
        try {
            api.signOut()
            settings.remove("jwt")
            SupabaseClient.client.auth.signOut()
        } catch (_: Exception) {
            // intentionally ignored
        }
    }

    override suspend fun updateProfile(request: UpdateProfileRequest): Boolean {
        val token = getAuthToken() ?: return false
        return try {
            val response = api.updateProfile(token, request)
            val backendUpdated = response.modified
            try {
                SupabaseClient.client.auth.updateUser {
                    data = buildJsonObject {
                        put("display_name", request.displayName)
                    }
                }
            } catch (_: Exception) {
                // intentionally ignored
            }
            backendUpdated
        } catch (e: Exception) {
            false
        }
    }

    override fun getSignedInUsername(): String? = settings.getStringOrNull("email")

    override suspend fun setAuthToken(token: String?) {
        if (token.isNullOrBlank()) {
            settings.remove("jwt")
        } else {
            settings.putString("jwt", token)
        }
    }

    override fun getAuthToken(): String? = settings.getStringOrNull("jwt")

    override suspend fun getOrganizationById(organizationId: String): Organization? {
        return try {
            api.getOrganizationById(organizationId)
        } catch (_: Exception) {
            null
        }
    }

    override suspend fun getOrganizationList(): List<Organization> {
        return try {
            api.getOrganizations()
        } catch (_: Exception) {
            emptyList()
        }
    }

    override suspend fun updateOrganization(
        request: UpdateOrganizationRequest
    ): Boolean {
        val token = getAuthToken() ?: return false
        return try {
            val response = api.updateOrganization(token, request)
            response.modified && response.organization != null
        } catch (_: Exception) {
            false
        }
    }

    private fun handleError(e: Exception): AuthResult<Profile> {
        return when {
            e.message?.contains("Invalid", true) == true -> AuthResult.InvalidCredentials()
            e.message?.contains("blank", true) == true -> when {
                e.message?.contains("email", true) == true -> AuthResult.EmailBlank()
                e.message?.contains("password", true) == true -> AuthResult.PasswordBlank()
                else -> AuthResult.UnknownError()
            }
            else -> {
                AuthResult.UnknownError()
            }
        }
    }

    override suspend fun getLeaderboard(): List<OrganizationLeaderboardItem> {
        return try {
            api.getLeaderboard()
        } catch (_: Exception) {
            emptyList()
        }
    }

    override suspend fun sendPasswordReset(email: String) {
        try {
            SupabaseClient.client.auth.resetPasswordForEmail(
                email = email,
                redirectUrl = "bashbuddyapp://reset" // KMP-friendly deep link
            )
        } catch (e: Exception) {
            throw e
        }
    }

    override suspend fun updatePassword(newPassword: String, recoveryToken: String) {
        if (recoveryToken.isEmpty()) {
            throw IllegalArgumentException("Recovery token cannot be empty")
        }
        try {
            SupabaseClient.client.auth.importAuthToken(
                accessToken = recoveryToken,
                refreshToken = "",
                retrieveUser = true,
                autoRefresh = false
            )
            SupabaseClient.client.auth.updateUser {
                this.password = newPassword
            }
            SupabaseClient.client.auth.signOut()
        } catch (e: Exception) {
            val message = e.message ?: "Unknown error"
            val friendlyMessage = when {
                message.contains("same_password", ignoreCase = true) ->
                    "New password cannot be the same as your old password."
                message.contains("weak", ignoreCase = true) ->
                    "Password is too weak. Try adding numbers or symbols."
                message.contains("token", ignoreCase = true) ->
                    "Your password reset link may have expired. Please try again."
                message.contains("network", ignoreCase = true) ->
                    "Network error. Please check your connection."
                else -> message
            }
            throw Exception(friendlyMessage, e)
        }
    }

    override suspend fun deactivateAccount(): Boolean {
        val token = getAuthToken() ?: return false
        return api.deactivateAccount(token)
    }

    override suspend fun reactivateAccount(): Boolean {
        val token = getAuthToken() ?: return false
        return api.reactivateAccount(token)
    }

    override suspend fun deleteAccount(): Boolean {
        val token = getAuthToken() ?: return false
        return api.deleteAccount(token)
    }

}